import { apiClient } from './apiClient';

export type EmailSchedulingRunStatus = 'in_progress' | 'failed';

export interface EmailSchedulingStatus {
  status: EmailSchedulingRunStatus | null;
  isInProgress: boolean;
  task_id: string | null;
  message: string | null;
  percent_complete: number | null;
  task_data: Record<string, unknown>;
}

type EmailSchedulingWatchListener = () => void;

const emailSchedulingWatchListeners = new Set<EmailSchedulingWatchListener>();

/**
 * Signal that a reschedule was enqueued so status hooks can poll again
 * until the job appears / finishes (instead of polling forever while idle).
 */
export const notifyEmailSchedulingStarted = (): void => {
  emailSchedulingWatchListeners.forEach(listener => {
    try {
      listener();
    } catch {
      // Ignore listener errors so one subscriber cannot break others.
    }
  });
};

export const subscribeEmailSchedulingWatch = (
  listener: EmailSchedulingWatchListener
): (() => void) => {
  emailSchedulingWatchListeners.add(listener);
  return () => {
    emailSchedulingWatchListeners.delete(listener);
  };
};

const normalizeStatus = (
  raw: unknown,
  inProgress: boolean
): EmailSchedulingRunStatus | null => {
  if (raw === 'in_progress' || raw === 'failed') {
    return raw;
  }
  if (inProgress) {
    return 'in_progress';
  }
  return null;
};

export const getEmailSchedulingStatus = async (params: {
  orgId: string | number;
  effortId?: string | number | null;
  supporterId?: string | number | null;
}): Promise<EmailSchedulingStatus> => {
  const searchParams = new URLSearchParams();
  searchParams.set('org_id', String(params.orgId));
  if (params.effortId != null && params.effortId !== '') {
    searchParams.set('effort_id', String(params.effortId));
  }
  if (params.supporterId != null && params.supporterId !== '') {
    searchParams.set('supporter_id', String(params.supporterId));
  }

  const response = await apiClient(
    `/task/scheduling-status?${searchParams.toString()}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error('Failed to load email scheduling status');
  }
  const data = (await response.json()) as Record<string, unknown>;
  const isInProgress = Boolean(data['in_progress']);
  return {
    status: normalizeStatus(data['status'], isInProgress),
    isInProgress,
    task_id: (data['task_id'] as string | null | undefined) ?? null,
    message: (data['message'] as string | null | undefined) ?? null,
    percent_complete:
      (data['percent_complete'] as number | null | undefined) ?? null,
    task_data:
      (data['task_data'] as Record<string, unknown> | undefined) ?? {},
  };
};
